/** Responsive sales dashboard with an optional large-screen wallboard mode. */

import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadConfig } from '../config';
import { DashboardChart } from './DashboardChart';
import { countChartOption, salesChartOption } from './dashboard-charts';
import { formatUsdMinor } from './dashboard-format';
import { useDashboardRefresh, type DashboardCache } from './use-dashboard-refresh';
import {
  useDashboardApi,
  type DashboardSummary,
  type DashboardTimingAverage,
} from './dashboard-api';

const REFRESH_INTERVAL_MS = 60_000;
const WALLBOARD_REFRESH_INTERVAL_MS = 300_000;

export function DashboardPage({
  embeddedWallboard = false,
  refreshCache,
}: {
  embeddedWallboard?: boolean;
  refreshCache?: DashboardCache<DashboardSummary>;
} = {}): JSX.Element {
  const api = useDashboardApi();
  const location = useLocation();
  const navigate = useNavigate();
  const wallboard =
    embeddedWallboard || new URLSearchParams(location.search).get('display') === 'wallboard';
  const environment = loadConfig().environment.stage.toUpperCase();

  const { result, refreshing, refresh } = useDashboardRefresh(
    api.summary,
    wallboard ? WALLBOARD_REFRESH_INTERVAL_MS : REFRESH_INTERVAL_MS,
    refreshCache,
  );
  const summary = result?.ok ? result.data : null;
  const error = result && !result.ok ? (result.message ?? result.error) : null;

  const loading = summary === null;
  const salesOption = useMemo(
    () => salesChartOption(summary?.salesVolume ?? [], wallboard),
    [summary?.salesVolume, wallboard],
  );
  const listingsOption = useMemo(
    () => countChartOption(summary?.cardsListedVolume ?? [], 'listings', wallboard),
    [summary?.cardsListedVolume, wallboard],
  );
  const capturesOption = useMemo(
    () => countChartOption(summary?.cardCaptureVolume ?? [], 'captures', wallboard),
    [summary?.cardCaptureVolume, wallboard],
  );

  const toggleWallboard = (): void => {
    const search = new URLSearchParams(location.search);
    if (wallboard) search.delete('display');
    else search.set('display', 'wallboard');
    void navigate({ pathname: '/dashboard', search: search.toString() }, { replace: true });
  };

  return (
    <div className="tcvs-dashboard" data-wallboard={wallboard ? 'true' : 'false'}>
      <header className="tcvs-dashboard-heading">
        <div>
          <h2>Sales dashboard</h2>
          <p>
            <span className="tcvs-dashboard-environment">{environment}</span>
            {summary === null ? 'Loading current activity' : freshnessLabel(summary)}
            {refreshing ? ' · Refreshing' : ''}
          </p>
        </div>
        <div className="tcvs-dashboard-actions">
          <button type="button" onClick={() => void refresh(true)} disabled={refreshing}>
            Refresh
          </button>
          {embeddedWallboard ? null : (
            <button
              type="button"
              className="tcvs-icon-button"
              onClick={toggleWallboard}
              title={wallboard ? 'Exit wallboard view' : 'Open wallboard view'}
              aria-label={wallboard ? 'Exit wallboard view' : 'Open wallboard view'}
            >
              {wallboard ? '×' : '⛶'}
            </button>
          )}
        </div>
      </header>
      {error !== null ? <p className="tcvs-error">Refresh failed: {error}</p> : null}

      <div className="tcvs-stat-grid tcvs-dashboard-stat-grid">
        <StatCard
          label="Cards in storage"
          value={loading ? '…' : summary.cardCount.toLocaleString()}
        />
        <StatCard
          label="Awaiting identification"
          value={loading ? '…' : summary.awaitingIdentificationCount.toLocaleString()}
          tone={!loading && summary.awaitingIdentificationCount > 0 ? 'warning' : 'normal'}
        />
        <StatCard
          label="Cards listed"
          value={loading ? '…' : summary.listedCardCount.toLocaleString()}
        />
        <StatCard
          label="Listed value"
          value={loading ? '…' : formatUsdMinor(summary.listedCardValueMinor)}
        />
        <StatCard
          label="Daily sales"
          value={loading ? '…' : formatUsdMinor(summary.salesToday.amountMinor)}
          detail={loading ? undefined : formatSalesDetail(summary.salesToday)}
        />
        <StatCard
          label="Weekly sales"
          value={loading ? '…' : formatUsdMinor(summary.salesLast7Days.amountMinor)}
          detail={loading ? undefined : formatSalesDetail(summary.salesLast7Days)}
        />
        <StatCard
          label="Awaiting fulfillment"
          value={loading ? '…' : summary.awaitingFulfillmentCount.toLocaleString()}
        />
        <StatCard
          label="Sold awaiting pick"
          value={loading ? '…' : summary.soldAwaitingPickCount.toLocaleString()}
          tone={!loading && summary.soldAwaitingPickCount > 0 ? 'warning' : 'normal'}
        />
        <StatCard
          label="eBay exceptions"
          value={loading ? '…' : summary.unresolvedEbayExceptions.toLocaleString()}
          tone={!loading && summary.unresolvedEbayExceptions > 0 ? 'warning' : 'normal'}
        />
        <StatCard
          label="Avg capture interval"
          value={loading ? '…' : formatTimingAverage(summary.averageCaptureInterval)}
          detail={
            loading
              ? undefined
              : formatTimingDetail(summary.averageCaptureInterval, summary.lastBatchCaptureInterval)
          }
        />
      </div>

      <div className="tcvs-dashboard-chart-grid">
        <ChartCard title="Sales volume" subtitle="Daily revenue and 7-day average">
          <DashboardChart option={salesOption} label="Sales volume for the past 30 days" />
        </ChartCard>
        <ChartCard title="Cards listed" subtitle="New listings by day">
          <DashboardChart option={listingsOption} label="Cards listed for the past 30 days" />
        </ChartCard>
        <ChartCard title="Card captures" subtitle="Cards imaged by day">
          <DashboardChart option={capturesOption} label="Card captures for the past 30 days" />
        </ChartCard>
      </div>
    </div>
  );
}

function freshnessLabel(summary: DashboardSummary): string {
  const timestamps = [
    summary.marketplaceFreshness.inventoryUpdatedAt,
    summary.marketplaceFreshness.ordersUpdatedAt,
  ].filter((value): value is string => value !== undefined);
  if (timestamps.length === 0) return 'eBay sync pending';
  const oldest = Math.min(...timestamps.map((value) => Date.parse(value)));
  if (!Number.isFinite(oldest)) return 'eBay sync time unavailable';
  const minutes = Math.max(0, Math.round((Date.now() - oldest) / 60_000));
  return minutes < 1 ? 'eBay updated just now' : `eBay updated ${minutes}m ago`;
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: JSX.Element;
}): JSX.Element {
  return (
    <section className="tcvs-card tcvs-dashboard-chart-card">
      <header className="tcvs-dashboard-chart-header">
        <h3>{title}</h3>
        <span>{subtitle}</span>
      </header>
      {children}
    </section>
  );
}

function formatSalesDetail(total: { cardCount: number; orderCount: number }): string {
  const cards = `${total.cardCount.toLocaleString()} card${total.cardCount === 1 ? '' : 's'}`;
  const orders = `${total.orderCount.toLocaleString()} order${total.orderCount === 1 ? '' : 's'}`;
  return `${cards} · ${orders}`;
}

function StatCard({
  label,
  value,
  detail,
  tone = 'normal',
}: {
  label: string;
  value: string;
  detail?: string;
  tone?: 'normal' | 'warning';
}): JSX.Element {
  return (
    <div className="tcvs-card tcvs-stat-card" data-tone={tone}>
      <div className="tcvs-stat-label">{label}</div>
      <div className="tcvs-stat-value" data-compact={value.length >= 10 ? 'true' : 'false'}>
        {value}
      </div>
      {detail !== undefined ? <div className="tcvs-stat-detail">{detail}</div> : null}
    </div>
  );
}

function formatTimingAverage(metric: DashboardTimingAverage): string {
  return metric.sampleCount === 0 ? 'n/a' : formatDuration(metric.averageMs);
}

function formatTimingDetail(
  metric: DashboardTimingAverage,
  lastBatch: DashboardTimingAverage,
): string {
  if (metric.sampleCount === 0) return 'No samples in last 24h';
  return lastBatch.sampleCount === 0
    ? `${metric.sampleCount} samples · Last batch: n/a`
    : `${metric.sampleCount} samples · Last batch: ${formatDuration(lastBatch.averageMs)}`;
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return 'n/a';
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes.toFixed(minutes < 10 ? 1 : 0)}m`;
  return `${(minutes / 60).toFixed(minutes < 600 ? 1 : 0)}h`;
}
